import type { FormEvent } from "react";

import { useDialogForm, useToast } from "@/ds";
import { cadastrarCotacaoReferencia } from "@/modules/financeiro-configuracoes";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import { COTACAO_CADASTRADA, NOVA_COTACAO_FORM_ID } from "./nova-cotacao-dialog.constants";
import {
  NOVA_COTACAO_VAZIA,
  erroCompetencia,
  erroCotacao,
  erroMoeda,
  montarCorpo,
} from "./nova-cotacao-dialog.form";
import type { NovaCotacaoForm } from "./nova-cotacao-dialog.form";
import type {
  NovaCotacaoDialogProps,
  NovaCotacaoDialogViewModel,
} from "./types-nova-cotacao-dialog";

export function useNovaCotacaoDialog({
  moedas,
  onClose,
  onConcluido,
}: NovaCotacaoDialogProps): NovaCotacaoDialogViewModel {
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();

  const form = useDialogForm<NovaCotacaoForm>({
    initial: NOVA_COTACAO_VAZIA,
    validate: ({ competencia, moedaId, cotacao }) => ({
      competencia: erroCompetencia(competencia) ?? undefined,
      moedaId: erroMoeda(moedaId) ?? undefined,
      cotacao: erroCotacao(cotacao) ?? undefined,
    }),
    onSubmit: async (valores) => {
      const corpo = montarCorpo(valores);
      if (!corpo) return;
      await comChave(corpo, (idempotencyKey) =>
        cadastrarCotacaoReferencia(corpo, { idempotencyKey }),
      );
      toast.success(COTACAO_CADASTRADA);
      onConcluido();
    },
  });

  const { values } = form;
  const descarte = useConfirmarDescarte({
    onDescartar: onClose,
    testId: "nova-cotacao-descarte",
  });

  const enviar = (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    void form.submit();
  };

  return {
    formId: NOVA_COTACAO_FORM_ID,
    competencia: {
      valor: values.competencia,
      erro: form.errors.competencia ?? null,
      escolher: (data) => form.setValue("competencia", data),
    },
    moeda: {
      valor: values.moedaId,
      opcoes: moedas.map((moeda) => ({
        label: `${moeda.codigo} · ${moeda.nome}`,
        value: moeda.id,
      })),
      erro: form.errors.moedaId ?? null,
      escolher: (moedaId) => form.setValue("moedaId", moedaId),
    },
    cotacao: {
      valor: values.cotacao,
      erro: form.errors.cotacao ?? null,
      digitar: (valor) => form.setValue("cotacao", valor),
    },
    isSubmitting: form.isSubmitting,
    submitError: form.submitError,
    enviar,
    cancelar: () => descarte.pedir(form.isDirty),
    descarte: descarte.confirmacao,
  };
}
